import CLIENT from '../utils/CUP';
import REST_CLIENT from '../utils/restClient';

export const MODULE_NAME = 'USER_MANAGEMENT_CONTROLLER';

function namespaced(name) {
    return MODULE_NAME + '.' + name;
}

const LOAD = namespaced('LOAD');
const LOAD_OK = namespaced('LOAD_OK');
const LOAD_FAIL = namespaced('LOAD_FAIL');
const LOAD_STOP = namespaced('LOAD_STOP');
const LOAD_COMPANY_DETAILS_OK = namespaced('LOAD_COMPANY_DETAILS_OK');
const LOAD_COMPANY_DETAILS_FAIL = namespaced('LOAD_COMPANY_DETAILS_FAIL');
const LOAD_IAM_TENANT_INFO = namespaced('LOAD_IAM_TENANT_INFO');
const LOAD_IAM_TENANT_INFO_OK = namespaced('LOAD_IAM_TENANT_INFO_OK');
const LOAD_IAM_TENANT_INFO_FAIL = namespaced('LOAD_IAM_TENANT_INFO_FAIL');

export const ACTION_TYPES = {
    LOAD,
    LOAD_OK,
    LOAD_FAIL,
    LOAD_STOP,
    LOAD_COMPANY_DETAILS_OK,
    LOAD_COMPANY_DETAILS_FAIL,
    LOAD_IAM_TENANT_INFO,
    LOAD_IAM_TENANT_INFO_OK,
    LOAD_IAM_TENANT_INFO_FAIL
};

export const ADD_USER_BLOCK_STATE = {
    PENDING: 'PENDING',
    ALLOWED: 'ALLOWED',
    BLOCKED: 'BLOCKED'
};

const initialIamTenantInfo = { loading: false, loaded: false, iamTenantId: null, error: null };

// Fails closed: a failed IamTenantInfo request blocks Add User.
export const getAddUserBlockState = (state) => {
    const info = state.userManagement?.iamTenantInfo;
    if (!info || !info.loaded || info.loading) {
        return ADD_USER_BLOCK_STATE.PENDING;
    }
    if (info.error || (typeof info.iamTenantId === 'string' && info.iamTenantId.trim() !== '')) {
        return ADD_USER_BLOCK_STATE.BLOCKED;
    }
    return ADD_USER_BLOCK_STATE.ALLOWED;
};

export const userManagementReducer = (state = {
    data: undefined,
    loading: false,
    error: null,
    iamTenantInfo: initialIamTenantInfo,
}, action) => {
    switch (action.type) {
        case LOAD:
            return Object.assign({}, state, { loading: true, error: null });
        case LOAD_OK:
            return Object.assign({}, state, { data: action.response, loading: false, error: null });
        case LOAD_FAIL:
            return Object.assign({}, state, { data: undefined, loading: false, error: action.error });
        case LOAD_STOP:
            return Object.assign({}, state, { loading: false });
        case LOAD_COMPANY_DETAILS_OK:
            const allCompanies = action.response;
            const companiesLookup = allCompanies ?
                Object.fromEntries(allCompanies.map((company) =>
                    [company.id, `${company.code} - ${company.name}`]
                )) :
                {};
            return Object.assign({}, state, { allCompanies: allCompanies, companiesLookup: companiesLookup, loading: false, error: null });
        case LOAD_COMPANY_DETAILS_FAIL:
            return Object.assign({}, state, { allCompanies: undefined, loading: false });
        case LOAD_IAM_TENANT_INFO:
            return Object.assign({}, state, { iamTenantInfo: { ...initialIamTenantInfo, loading: true } });
        case LOAD_IAM_TENANT_INFO_OK:
            return Object.assign({}, state, { iamTenantInfo: { ...initialIamTenantInfo, loaded: true, iamTenantId: action.iamTenantId } });
        case LOAD_IAM_TENANT_INFO_FAIL:
            return Object.assign({}, state, { iamTenantInfo: { ...initialIamTenantInfo, loaded: true, error: action.error } });
        default:
            return state;
    }
};

export const API = {

    doLoad: () => {
        return async (dispatch, getState) => {
            dispatch({ type: LOAD });
            try {
                const response = await CLIENT.getUsers(2);
                dispatch({ type: LOAD_OK, response: response });
            } catch (error) {
                dispatch({ type: LOAD_FAIL, error });
            }
        }
    },

    doEdit: (user, successHandler, errorHandler) => {
        return (dispatch) => {
            dispatch({ type: LOAD });
            CLIENT.updateUser(user.id, user, 2)
                .then((response) => {
                    dispatch({ type: LOAD_STOP });
                    successHandler(response);
                })
                .catch((error) => {
                    dispatch({ type: LOAD_STOP });
                    errorHandler(error);
                });
        };
    },

    doDeleteUser: (userId, successHandler, errorHandler) => {
        return async (dispatch) => {
            dispatch({ type: LOAD });
            try {
                const response = await CLIENT.deleteUser(userId, 2);
                dispatch({ type: LOAD_STOP });
                successHandler(response);
            } catch (error) {
                dispatch({ type: LOAD_STOP });
                errorHandler(error);
            }
        };
    },

    doLoadCompanies: () => {
        return async (dispatch, getState) => {
            dispatch({ type: LOAD });
            try {
                const response = await REST_CLIENT.getCompaniesSummaryV1();
                dispatch({ type: LOAD_COMPANY_DETAILS_OK, response: response });
            } catch (error) {
                dispatch({ type: LOAD_COMPANY_DETAILS_FAIL });
            }
        }
    },

    // Loaded once per session; a failure is terminal so the UI stays blocked (fail closed).
    doLoadIamTenantInfo: () => {
        return async (dispatch, getState) => {
            const info = getState().userManagement?.iamTenantInfo;
            if (info?.loaded || info?.loading) {
                return;
            }
            dispatch({ type: LOAD_IAM_TENANT_INFO });
            try {
                const response = await REST_CLIENT.getIamTenantInfo();
                dispatch({ type: LOAD_IAM_TENANT_INFO_OK, iamTenantId: response.iamTenantId });
            } catch (error) {
                dispatch({ type: LOAD_IAM_TENANT_INFO_FAIL, error });
            }
        }
    },

    doCreate: (user, successCallback, failureCallback) => {
        return async () => {
            try {
                const response = await CLIENT.createUser(user);
                if (response.resetToken) {
                    successCallback();
                } else {
                    failureCallback(response);
                }
            } catch (error) {
                failureCallback(error);
            }
        }
    }
};

export default API;
